import dayjs from 'dayjs'
import {
	IFeatureAccessDto,
	IJurisdictionDto,
	IJurisdictionPropertiesDto,
	ITutotialStatusDto,
	IUserDto,
	IUserInviteDto,
} from './dtos'
import {
	IFeatureAccess,
	IJurisdiction,
	IJurisdictionProperties,
	ITutotialStatus,
	IUser,
	IUserInvite,
	SupportedJurisdictions,
} from './models'

export const mapUserFromDto = ({
	userId,
	firstName,
	lastName,
	currentPlan,
	subscribedPlan,
	email,
	isCpa,
	referral,
	jurisdiction,
	properties,
	costBasisMethod,
	numberOfRefreshesAllowed,
	numberOfRefreshesUsed,
	numberOfTransactionsAllowed,
	organizationId,
	paidUntil,
	hasCustomerId,
	externalProvider,
}: IUserDto): IUser => ({
	id: userId,
	firstName,
	lastName,
	fullName: `${firstName} ${lastName}`,
	currentPlan,
	subscribedPlan,
	email,
	isCPA: isCpa,
	referral,
	jurisdiction: jurisdiction as SupportedJurisdictions,
	properties,
	costBasisMethod,
	maxAnalyzeCount: numberOfRefreshesAllowed,
	maxTransactions: numberOfTransactionsAllowed,
	usedAnalyzeCount: numberOfRefreshesUsed,
	orgId: organizationId || undefined,
	paidUntil: paidUntil ? dayjs(paidUntil).toDate() : undefined,
	hasCustomerId: !!hasCustomerId,
	externalProvider: externalProvider?.type
		? {
				type: externalProvider.type!,
				userId: externalProvider.userId!,
		  }
		: undefined,
})

export const mapUserInviteFromDto = ({ email, retries, sender, status, id }: IUserInviteDto): IUserInvite => ({
	email,
	retries,
	resentRecently: false,
	sender,
	status,
	id,
})

export const mapJurisdictionFromDto = ({
	allowedCostBasisMethods,
	shortName,
	fullName,
	defaultCurrency,
}: IJurisdictionDto): IJurisdiction => ({
	shortName: shortName as SupportedJurisdictions,
	fullName,
	costBasisMethods: allowedCostBasisMethods.map(({ costBasisMethod, isOfficiallyAllowed }) => ({
		name: costBasisMethod,
		isAllowed: isOfficiallyAllowed,
	})),
	currency: defaultCurrency,
})

export const mapJurisdictionPropertiesFromDto = ({
	userProperties,
	transactionProperties,
	walletProperties,
}: IJurisdictionPropertiesDto): IJurisdictionProperties => ({
	user: userProperties,
	transcaction: transactionProperties,
	wallet: walletProperties,
})

export const mapFeatureAccessFromDto = ({
	downloadReportsAccessible,
	reviewTransactionAccessible,
	addTransaction,
	deleteAll,
	deleteAllIntegrations,
	deleteIntegration,
	deleteTransaction,
	generateReport,
	integrateBlockchainApiTransactions,
	integrateExchangeApiTransactions,
	integrateExchangeCsvTransactions,
	integrateTemplateCsvTransactions,
	integrateWalletCsvTransactions,
	modifyTransaction,
	performTaxAnalysis,
	performTransferMapping,
	recalculateValuations,
}: IFeatureAccessDto): IFeatureAccess => ({
	download: downloadReportsAccessible,
	reviewTransactions: reviewTransactionAccessible,
	addTransaction,
	editTransaction: modifyTransaction,
	deleteTransaction,
	deleteAllTransactions: deleteAll,
	deleteIntegration,
	deleteAllIntegrations,
	import:
		integrateBlockchainApiTransactions ||
		integrateExchangeApiTransactions ||
		integrateExchangeCsvTransactions ||
		integrateTemplateCsvTransactions ||
		integrateWalletCsvTransactions,
	generateReport,
	taxAnalysis: performTaxAnalysis,
	transferMapping: performTransferMapping,
	recalculate: recalculateValuations,
})

export const mapTutorialStatusFromDto = ({ reports, transactions, imports }: ITutotialStatusDto): ITutotialStatus => ({
	reports,
	transactions,
	imports,
})
